import React, { useEffect, useRef } from 'react';
import { generatePDFFromPreview, generatePDFFromPreviewAsBlob } from './pdfGenerator';
import type { ResumeData, Template, UserOverrides } from './resumeTypes';
import ResumeRenderer from './ResumeRenderer';

/** Размеры A4 в px для корректного layout (как в билдере на этапе 3 консультанта). */
const A4_WIDTH_PX = Math.round(210 * 3.779527559);
const A4_HEIGHT_PX = Math.round(297 * 3.779527559);

export interface PdfExportFromPreviewLayerProps {
  resume: ResumeData;
  template: Template;
  overrides: UserOverrides;
  /** 'download' — сохранить файл; 'view' — открыть в новой вкладке */
  mode?: 'download' | 'view';
  onDone: () => void;
  onError: (message: string) => void;
}

/**
 * Слой для экспорта PDF так же, как в резюме-билдере (Мои резюме / этап 3 консультанта).
 * Рендерит превью через A4Preview + ResumeRenderer, ждёт готовности layout, затем
 * клонирует этот DOM в buildPDFFromPreview — поля и вид совпадают с билдером.
 */
const PdfExportFromPreviewLayer: React.FC<PdfExportFromPreviewLayerProps> = ({
  resume,
  template,
  overrides,
  mode = 'download',
  onDone,
  onError,
}) => {
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    let cancelled = false;

    const waitForSheetReady = (): Promise<boolean> => {
      return new Promise((resolve) => {
        const deadline = Date.now() + 3000;
        const tick = () => {
          if (cancelled) {
            resolve(false);
            return;
          }
          const el = document.querySelector('.a4-preview-container[data-pdf-export="true"] .a4-sheet') as HTMLElement | null;
          if (el && el.offsetHeight > 0) {
            resolve(true);
            return;
          }
          if (Date.now() >= deadline) {
            resolve(!!el);
            return;
          }
          setTimeout(tick, 100);
        };
        setTimeout(tick, 300);
      });
    };

    const run = async () => {
      try {
        const ready = await waitForSheetReady();
        if (cancelled || !ready) {
          if (!cancelled) onError('Таймаут подготовки превью');
          onDone();
          return;
        }
        if (mode === 'download') {
          await generatePDFFromPreview(resume, template, overrides);
        } else {
          const blob = await generatePDFFromPreviewAsBlob(resume, template, overrides);
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
        if (!cancelled) onDone();
      } catch (e) {
        if (!cancelled) {
          console.error('Ошибка экспорта PDF:', e);
          onError(e instanceof Error ? e.message : 'Не удалось сформировать PDF');
        }
        onDone();
      }
    };
    run();
    return () => { cancelled = true; doneRef.current = true; };
  }, [resume, template, overrides, mode, onDone, onError]);

  return (
    <div
      data-pdf-export="true"
      className="a4-preview-container"
      style={{
        position: 'fixed',
        left: '-100000px',
        top: 0,
        width: `${A4_WIDTH_PX}px`,
        minHeight: `${A4_HEIGHT_PX}px`,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'visible',
      }}
      aria-hidden={true}
    >
      <ResumeRenderer data={resume} template={template} overrides={overrides} />
    </div>
  );
};

export default PdfExportFromPreviewLayer;
